"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export interface LivePrice {
  price: number;
  change24h: number;
}

type PriceMap = Record<string, LivePrice>;

const PricesContext = createContext<PriceMap>({});

/** Live prices keyed by asset symbol (e.g. "SOL"). Empty until first fetch. */
export const useLivePrices = () => useContext(PricesContext);

const REFRESH_MS = 60_000;

/**
 * Fetches cached live prices from /api/prices once on mount and then every
 * minute, sharing them app-wide. Soft-fails silently — consumers fall back to
 * their curated prices when a symbol is missing.
 */
export function PricesProvider({ children }: { children: ReactNode }) {
  const [prices, setPrices] = useState<PriceMap>({});

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        // no-store so the browser always re-requests; the server still serves
        // its 60s-cached CoinGecko data, so the interval refresh works even with
        // the app's aggressive global Cache-Control header.
        const res = await fetch("/api/prices", { cache: "no-store" });
        if (!res.ok) return;
        const json = (await res.json()) as { prices?: PriceMap };
        if (active && json?.prices && Object.keys(json.prices).length > 0) {
          setPrices(json.prices);
        }
      } catch {
        // ignore — keep last good / curated fallbacks
      }
    };

    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  return <PricesContext.Provider value={prices}>{children}</PricesContext.Provider>;
}
